import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { AuthProvider } from "./context/AuthContext";
import { AudioProvider } from "./context/AudioContext";
import { NetworkProvider } from "./context/NetworkContext";
import { AchievementProvider } from "./context/AchievementContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Configure axios defaults for better performance
axios.defaults.timeout = 30000; // 30 second timeout
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Eager load critical pages
import { HomePage } from "./pages/HomePage";
import { LoginPage, RegisterPage, ForgotPasswordPage } from "./pages/AuthPages";

// Lazy load heavy pages for faster initial load
const CalculatorPage = lazy(() => import("./pages/CalculatorPage").then(m => ({ default: m.CalculatorPage })));
const ResultsPage = lazy(() => import("./pages/ResultsPage").then(m => ({ default: m.ResultsPage })));
const TarotPage = lazy(() => import("./pages/TarotPage").then(m => ({ default: m.TarotPage })));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage").then(m => ({ default: m.CompatibilityPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const SupportPage = lazy(() => import("./pages/SupportPage").then(m => ({ default: m.SupportPage })));
const CalculationsPage = lazy(() => import("./pages/CalculationsPage").then(m => ({ default: m.CalculationsPage })));
const SharedReadingPage = lazy(() => import("./pages/SharedReadingPage").then(m => ({ default: m.SharedReadingPage })));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage").then(m => ({ default: m.PublicProfilePage })));
const AdminPanelPage = lazy(() => import("./pages/AdminPanelPage").then(m => ({ default: m.AdminPanelPage })));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage").then(m => ({ default: m.UserProfilePage })));
const GamesPage = lazy(() => import("./pages/GamesPage").then(m => ({ default: m.GamesPage })));
const SolitairePage = lazy(() => import("./pages/SolitairePage").then(m => ({ default: m.SolitairePage })));
const MahjongPage = lazy(() => import("./pages/MahjongPage").then(m => ({ default: m.MahjongPage })));
const RPGPage = lazy(() => import("./pages/RPGPage").then(m => ({ default: m.RPGPage })));
const TextAdventurePage = lazy(() => import("./pages/TextAdventurePage"));
const LearnTarotPage = lazy(() => import("./pages/LearnTarotPage").then(m => ({ default: m.LearnTarotPage })));
const OraclePage = lazy(() => import("./pages/OraclePage").then(m => ({ default: m.OraclePage })));
const BringAFriendPage = lazy(() => import("./pages/BringAFriendPage").then(m => ({ default: m.BringAFriendPage })));

// Global components (always needed)
import { GlobalNavbar } from "./components/GlobalNavbar";
import { AudioControls } from "./components/AudioControls";
import { ContactAdminButton } from "./components/ContactAdminButton";
import { AdminInbox } from "./components/AdminInbox";
import { GlobalActivityTicker } from "./components/GlobalActivityTicker";
import { CosmicQuestLedger } from "./components/CosmicQuestLedger";
import WhatsNewModal from "./components/WhatsNewModal";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-babylon-obsidian">
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin text-babylon-gold mx-auto mb-2" />
      <p className="text-sm text-babylon-text-dim">Loading...</p>
    </div>
  </div>
);

const API = process.env.REACT_APP_BACKEND_URL;

// Import activity tracker hook
import { useActivityTracker } from "./hooks/useActivityTracker";

// Track visitor on app load
const useVisitorTracking = () => {
  useEffect(() => {
    // Capture ?ref=USERNAME from any landing page so it persists through to /register.
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        const clean = ref.trim().toLowerCase().slice(0, 30);
        if (clean) localStorage.setItem('cosmicards_ref', clean);
      }
    } catch {}

    const trackVisitor = async () => {
      try {
        // Only track once per session
        if (sessionStorage.getItem('visitor_tracked')) return;
        
        await axios.post(`${API}/api/analytics/track-visitor`);
        sessionStorage.setItem('visitor_tracked', 'true');
      } catch (error) {
        // Silently fail - visitor tracking is not critical
      }
    };
    trackVisitor();
  }, []);
};

// Track page views for authenticated users
const usePageViewTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    const trackPageView = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return; // Only track for logged-in users
        
        await axios.post(
          `${API}/api/analytics/track-page`,
          null,
          { 
            headers: { Authorization: `Bearer ${token}` },
            params: { page: location.pathname }
          }
        );
      } catch (error) {
        // Silently fail
      }
    };
    
    // Debounce to avoid tracking rapid navigations
    const timeoutId = setTimeout(trackPageView, 1000);
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
};

// Pages where navbar should be hidden
const HIDE_NAVBAR_PATHS = ['/login', '/register', '/forgot-password', '/games/solitaire', '/games/mahjong', '/games/rpg'];

// Game pages where floating chat/UI should be hidden
const GAME_PATHS = ['/games/solitaire', '/games/mahjong', '/games/rpg'];

const NavbarWrapper = () => {
  const location = useLocation();
  const hideNavbar = HIDE_NAVBAR_PATHS.some(path => location.pathname === path || location.pathname.startsWith(path + '/'));
  
  if (hideNavbar) return null;
  return <GlobalNavbar />;
};

// Legacy route alias: /rpg → /games/rpg, preserving query string so
// previously-sent push notifications and bookmarks resolve to the right page.
const LegacyRpgRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/games/rpg${search}`} replace />;
};

const FloatingUIWrapper = () => {
  const location = useLocation();
  const isGame = GAME_PATHS.some(path => location.pathname === path || location.pathname.startsWith(path + '/'));
  
  if (isGame) return null;
  return (
    <>
      <ContactAdminButton />
      <AdminInbox />
      <AudioControls />
      <GlobalActivityTicker />
    </>
  );
};

// Component to enable page view tracking (must be inside Router)
const PageViewTracker = () => {
  usePageViewTracking();
  return null;
};

// Component to enable real-time activity tracking (must be inside Router)
const ActivityTracker = () => {
  useActivityTracker();
  return null;
};

function App() {
  // Track visitor on app load
  useVisitorTracking();
  
  return (
    <HelmetProvider>
    <NetworkProvider>
      <AuthProvider>
        <AchievementProvider>
        <AudioProvider>
          <BrowserRouter>
          <PageViewTracker />
          <ActivityTracker />
          <div className="App">
            {/* Global Navigation */}
            <NavbarWrapper />
            
            <Suspense fallback={<PageLoader />}>
            <div className="site-content-container">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/calculator" element={<CalculatorPage />} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/tarot" element={<TarotPage />} />
              <Route path="/oracle" element={<OraclePage />} />
              <Route path="/compatibility" element={<CompatibilityPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/user/:userId" element={<UserProfilePage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/calculations" element={<CalculationsPage />} />
              <Route path="/shared/:shareId" element={<SharedReadingPage />} />
              <Route path="/u/:username" element={<PublicProfilePage />} />
              <Route path="/admin" element={<AdminPanelPage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/solitaire" element={<SolitairePage />} />
              <Route path="/games/mahjong" element={<MahjongPage />} />
              <Route path="/games/rpg" element={<RPGPage />} />
              {/* Legacy /rpg alias — preserves the query string so old push
                  notifications and bookmarks keep working after the route
                  was moved under /games/. */}
              <Route path="/rpg" element={<LegacyRpgRedirect />} />
              <Route path="/games/astral-chronicles" element={<TextAdventurePage />} />
              <Route path="/learn" element={<LearnTarotPage />} />
              <Route path="/recruit" element={<BringAFriendPage />} />
              <Route path="/bring-a-friend" element={<BringAFriendPage />} />
            </Routes>
            </div>
            </Suspense>
            
            {/* Global UI Components - hidden on game pages */}
            <FloatingUIWrapper />
            
            {/* Live ledger — visible everywhere on wide desktop viewports */}
            <CosmicQuestLedger />
            {/* Auto-show release notes once per build */}
            <WhatsNewModal />
          </div>
          <Toaster 
            position="top-right"
            style={{ pointerEvents: 'none' }}
            toastOptions={{
              style: {
                background: 'rgba(0, 0, 0, 0.65)',
                backdropFilter: 'blur(12px)',
                border: 'none',
                color: '#fff',
                fontSize: '0.75rem',
                borderRadius: '9999px',
                padding: '6px 16px',
                boxShadow: 'none',
                pointerEvents: 'none',
              }
            }}
          />
        </BrowserRouter>
        </AudioProvider>
        </AchievementProvider>
      </AuthProvider>
    </NetworkProvider>
    </HelmetProvider>
  );
}

export default App;
// Force rebuild v3 - 1767737250
